import React, { useCallback, useEffect, useState } from 'react';
import { Button, Badge } from '@/components';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';
import { useDebouncedValue } from '@/hooks/urlState';
import {
  ProviderUser,
  getProviderUsers,
  deleteProviderUser,
} from '@/services/api/providers';
import DeleteConfirmModal from './DeleteConfirmModal';
import ProviderUserFormModal from './ProviderUserFormModal';
import ChangeUserPasswordModal from './ChangeUserPasswordModal';

interface ProviderUsersTabProps {
  providerId: number | string;
}

export const ProviderUsersTab: React.FC<ProviderUsersTabProps> = ({
  providerId,
}) => {
  const [users, setUsers] = useState<ProviderUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  // Modals
  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ProviderUser | null>(null);
  const [pwdUser, setPwdUser] = useState<ProviderUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProviderUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProviderUsers(providerId, {
        search: debouncedSearch || undefined,
      });
      setUsers(res.data ?? []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load user accounts');
    } finally {
      setLoading(false);
    }
  }, [providerId, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateModal = () => {
    setEditingUser(null);
    setFormOpen(true);
  };

  const openEditModal = (u: ProviderUser) => {
    setEditingUser(u);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteProviderUser(providerId, deleteTarget.id);
      toast.success('User account deleted');
      setDeleteTarget(null);
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const inputCls =
    'w-full rounded-gsm border border-stroke-soft-200 bg-white px-3 py-2 text-sm text-text-strong-950 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

  const columns: TableDataColumn<ProviderUser>[] = [
    {
      key: 'index',
      title: 'No',
      width: '60px',
      render: (_v, _r, index) => (
        <span className="font-mono text-xs tabular text-text-sub-600">
          {index + 1}
        </span>
      ),
    },
    {
      key: 'name',
      title: 'User',
      render: (_v, record) => (
        <div>
          <span className="font-medium text-text-strong-950">
            {record.name}
          </span>
          <p className="text-xs text-text-sub-600">{record.email}</p>
          {record.phoneNumber && (
            <p className="font-mono text-[11px] tabular text-text-sub-600">
              +{record.phoneNumber}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'outlet',
      title: 'Outlet Scope',
      render: (_v, record) => (
        <span className="text-xs text-text-sub-600 font-medium">
          {record.providerOutlet?.name ?? 'All Outlets'}
        </span>
      ),
    },
    {
      key: 'pitstops',
      title: 'Pitstops',
      render: (_v, record) => {
        const pitstops = record.providerPitstops ?? [];
        if (pitstops.length === 0) {
          return <span className="text-text-sub-600">—</span>;
        }
        return (
          <span className="text-xs text-text-sub-600 font-medium">
            {pitstops.map((p) => p.name).join(', ')}
          </span>
        );
      },
    },
    {
      key: 'role',
      title: 'Role',
      width: '140px',
      render: (_v, record) => {
        const role = record.roles?.[0];
        if (!role) return <span className="text-text-sub-600">—</span>;
        return (
          <Badge tone="primary" size="small">
            {role.label || role.name}
          </Badge>
        );
      },
    },
    {
      key: 'createdAt',
      title: 'Created Date',
      render: (_v, record) => {
        const val = record.createdAt || record.created_at;
        if (!val) return <span className="text-text-sub-600">—</span>;
        const date = new Date(val);
        return (
          <span className="font-mono text-xs tabular text-text-sub-600">
            {date.toLocaleDateString('id-ID', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        );
      },
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '120px',
      align: 'right',
      render: (_v, record) => (
        <div className="inline-flex items-center gap-1">
          <Button
            variant="ghost"
            size="xs"
            onClick={(e) => {
              e?.stopPropagation();
              setPwdUser(record);
            }}
            aria-label="Change password"
          >
            <i className="ri-key-line text-[14px]" />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={(e) => {
              e?.stopPropagation();
              openEditModal(record);
            }}
            aria-label="Edit user"
          >
            <i className="ri-pencil-line text-[14px]" />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            className="text-red-600 hover:text-red-700"
            onClick={(e) => {
              e?.stopPropagation();
              setDeleteTarget(record);
            }}
            aria-label="Delete user"
          >
            <i className="ri-delete-bin-line text-[14px]" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-xs">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search user name or email…"
            aria-label="Search portal users"
            className={inputCls}
          />
        </div>
        <Button onClick={openCreateModal} data-testid="provider-user-add">
          <i className="ri-user-add-line mr-1.5" />
          Add Portal User
        </Button>
      </div>

      {/* Users Table */}
      <div className="rounded-gsm border border-stroke-soft-200 bg-white">
        <TableData
          columns={columns}
          data={users}
          loading={loading}
          emptyState={
            <div className="p-8 text-center text-text-sub-600">
              <i className="ri-team-line text-3xl mb-2 text-text-sub-600/50" />
              <p className="text-sm font-medium">
                {debouncedSearch
                  ? `No portal users match “${debouncedSearch}”`
                  : 'No portal users created'}
              </p>
            </div>
          }
        />
      </div>

      <ProviderUserFormModal
        isOpen={formOpen}
        providerId={providerId}
        user={editingUser}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          fetchData();
        }}
      />

      <ChangeUserPasswordModal
        isOpen={Boolean(pwdUser)}
        providerId={providerId}
        user={pwdUser}
        onClose={() => setPwdUser(null)}
        onSaved={() => setPwdUser(null)}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete User Account?"
        subtitle={deleteTarget?.name}
        description="Are you sure you want to delete this user? They will lose access to the client portal immediately."
        confirmText="Yes, Delete User"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ProviderUsersTab;
